import {
  Box,
  CircularProgress,
  Stack,
  SxProps,
  Tab,
  Tabs,
  Typography
} from '@mui/material';
import { Button } from 'system/atoms';
import React, { cloneElement, Fragment } from 'react';
import { MagicButton } from 'common/ui/MagicButton';

const tabsV2ContainerSx: SxProps = {
  borderTop: '1px solid',
  borderColor: 'primary.main'
};

const tabsV2ButtonActiveSx: SxProps = {
  borderColor: 'primary.main',
  color: 'customGrey.dark'
};

const tabsV2ButtonCountSx: SxProps = {
  backgroundColor: 'customGrey.light',
  p: '8px',
  fontWeight: 500,
  color: 'primary.main'
};

const tabsV2ButtonCountActiveSx: SxProps = {
  color: 'customGrey.dark'
};

const endItemButtonSx: SxProps = {
  fontSize: '16px',
  lineHeight: '24px'
};

const tabsListSx: SxProps = {
  borderTop: '1px solid primary.main',
  '& .MuiTabs-indicator': {
    width: 'full',
    height: '6px',
    left: 0,
    top: 0
  },
  '& .MuiTab-root.Mui-selected': {
    backgroundColor: 'white'
  }
};

const tabSx: SxProps = {
  alignItems: 'flex-start',
  textAlign: 'left',
  width: 'fit-content',
  minWidth: 'auto',
  borderBottom: 'none',
  borderTop: '6px solid transparent',
  '&:hover': {
    backgroundColor: 'action.hover'
  },
  padding: '0px !important'
};

const tabLabelSx: SxProps = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  width: 'fit-content',
  textTransform: 'none',
  padding: '12px',
  paddingTop: '15px'
};

const tabTextSx: SxProps = {
  fontSize: '14px',
  lineHeight: '20px',
  fontWeight: 500
};

export type TabV2Item = {
  label: string;
  value: string;
  count?: number | string;
  startIcon?: React.ReactNode | string;
  isCountLoading?: boolean;
};

export type TabV2ButtonItem =
  | React.ReactElement<typeof Button>
  | React.ReactElement<typeof MagicButton>
  | React.ReactElement;

type TabV2Props = {
  containerSx?: SxProps;
  tabs: TabV2Item[];
  value: string;
  endItems?: TabV2ButtonItem[];
  endItemsSx?: SxProps;
  onChange: (value: string) => void;
};

export const TabsV2: React.FC<TabV2Props> = ({
  containerSx,
  tabs,
  value,
  endItems,
  endItemsSx,
  onChange
}) => {
  const activeTab = tabs.find((tab) => tab.value === value);
  return (
    <Stack
      sx={{
        ...tabsV2ContainerSx,
        ...containerSx
      }}
      direction="row"
      justifyContent="space-between"
    >
      <Tabs
        orientation="horizontal"
        value={activeTab?.value}
        onChange={(_event, value) => {
          onChange(value);
        }}
        sx={tabsListSx}
        variant="scrollable"
        scrollButtons={false}
      >
        {tabs.map((item) => (
          <Tab
            key={item.value}
            value={item.value}
            disableRipple
            sx={{
              ...tabSx,
              ...(activeTab?.value === item.value && tabsV2ButtonActiveSx)
            }}
            label={
              <Box
                sx={{
                  ...tabLabelSx,
                  ...tabTextSx,
                  gap:
                    item.isCountLoading || Number.isInteger(item.count)
                      ? '8px'
                      : '0px'
                }}
              >
                {item.startIcon}
                <Typography
                  sx={tabTextSx}
                  color={
                    activeTab?.value === item.value
                      ? 'customGrey.dark'
                      : 'primary.main'
                  }
                >
                  {item.label}
                </Typography>
                {item.isCountLoading && (
                  <Box
                    sx={{
                      ...tabsV2ButtonCountSx,
                      ...(activeTab?.value === item.value &&
                        tabsV2ButtonCountActiveSx),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <CircularProgress size={12} color="inherit" />
                  </Box>
                )}
                {!item.isCountLoading && Number.isInteger(item.count) && (
                  <Box
                    sx={{
                      ...tabsV2ButtonCountSx,
                      ...(activeTab?.value === item.value &&
                        tabsV2ButtonCountActiveSx)
                    }}
                  >
                    {item.count}
                  </Box>
                )}
              </Box>
            }
          />
        ))}
      </Tabs>
      <Stack direction="row" gap="10px" alignItems="center">
        {endItems?.map((element, index) => {
          return (
            <Fragment key={index}>
              {cloneElement(element as React.ReactElement, {
                sx: [endItemsSx ? endItemsSx : endItemButtonSx]
              })}
            </Fragment>
          );
        })}
      </Stack>
    </Stack>
  );
};
